import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Lock, 
  ShieldCheck, 
  ArrowLeft, 
  Eye, 
  EyeOff, 
  Loader2
} from 'lucide-react';
import axiosInstance from '../../../api/axiosInstance';
import logo from '../../../assets/Easymarg Whitebg.png';
import bgimg from '../../../assets/login.png';

const ResetPassword = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState(''); // 'success' or 'error'
    const [loading, setLoading] = useState(false);

    // If user is already logged in, redirect them away from reset page
    useEffect(() => {
        const existingToken = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (existingToken) {
            navigate('/home', { replace: true });
        }
    }, [navigate]);

    // Block browser back button to login page
    useEffect(() => {
        const pushStates = () => {
            for (let i = 0; i < 10; i++) {
                window.history.pushState(null, null, window.location.pathname);
            }
        };
        
        pushStates();
        
        const handlePopState = () => {
            pushStates();
            navigate(window.location.pathname, { replace: true });
        };
        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        setMessageType('');

        if (!token) {
            setMessageType('error');
            setMessage("Invalid or missing reset token.");
            return;
        }
if (password.length < 8) {
    setMessageType("error");
    setMessage("Password must be at least 8 characters long.");
    return;
}

        if (password !== confirmPassword) {
            setMessageType('error');
            setMessage("Passwords do not match.");
            return;
        }

        setLoading(true);
        try {
            const { data } = await axiosInstance.put(`/users/reset-password/${token}`, {
                password: password
            });

            if (data.success) {
                setMessageType('success');
                setMessage("Password updated successfully! Redirecting to login...");
                setTimeout(() => {
                    navigate('/', { replace: true });
                }, 3000);
            }
        } catch (error) {
            setMessageType('error');
            setMessage(error.response?.data?.message || "Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const fadeInUp = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.2,
            },
        },
    };

    return (
        <div className="h-screen w-full flex flex-col lg:flex-row overflow-hidden font-outfit">
            
            {/* Left Brand Panel */}
            <div
                className="hidden lg:block relative w-full lg:w-1/2 lg:h-full shrink-0 bg-cover bg-center overflow-hidden"
                style={{ backgroundImage: `url(${bgimg})` }}
            >
                <div className="absolute inset-0 bg-gradient-to-br from-[#0a2463]/95 via-[#134DA7]/90 to-[#0a2463]/95 backdrop-blur-sm"></div>

                <div className="absolute top-5 left-5 sm:top-8 sm:left-8 z-20 bg-white/95 backdrop-blur-sm rounded-2xl px-4 py-2.5 shadow-lg">
                    <img src={logo} alt="EasyMarg Logo" className="h-6 sm:h-7" />
                </div>

                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={staggerContainer}
                    className="relative z-10 h-full flex flex-col items-center lg:items-start justify-center text-white text-center lg:text-left px-6 sm:px-10 lg:pl-16 lg:pr-10 xl:pl-24"
                >
                    <motion.h1 variants={fadeInUp} className="text-3xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-3 lg:mb-6">
                        WELCOME <br /> <span className="font-light">TO </span>
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-200">EASYMARG</span>
                    </motion.h1>
                    <motion.p variants={fadeInUp} className="text-sm sm:text-lg font-light text-blue-100/90 max-w-lg mx-auto lg:mx-0">
                        Simplify your financial management with our innovative and secure platform.
                    </motion.p>
                </motion.div>
            </div>

            {/* Right Form Panel */}
            <div className="flex-1 min-h-0 flex flex-col items-center bg-gray-50 p-4 sm:p-8 lg:p-12">
                <div className="lg:hidden mb-6 mt-4 bg-white rounded-2xl px-5 py-3 shadow-sm border border-gray-100">
                    <img src={logo} alt="EasyMarg Logo" className="h-7" />
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    className="w-full max-w-[480px] max-h-full my-auto flex flex-col min-h-0"
                >
                    <div className="flex items-center gap-1.5 text-xs font-medium text-gray-400 mb-3 px-1 shrink-0">
                        <span>EasyMarg</span>
                        <span className="text-gray-300">/</span>
                        <span className="text-[#134DA7] font-semibold">Reset Password</span>
                    </div>

                    <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col min-h-0">
                        <div className="h-1.5 bg-gradient-to-r from-[#134DA7] to-[#66BBFC] shrink-0"></div>
                        <div className="auth-scrollbar flex-1 min-h-0 overflow-y-auto p-6 sm:p-8">
                            <div className="text-center mb-6">
                                <h2 className="text-2xl font-bold text-[#0a2463]">Reset Password</h2>
                                <p className="text-sm text-gray-500 mt-1">Create a new secure password</p>
                            </div>

                            {message && (
                                <div className={`mb-4 p-3 rounded-lg text-sm text-center border ${
                                    messageType === 'success' 
                                        ? 'bg-green-50 border-green-100 text-green-700' 
                                        : 'bg-red-50 border-red-100 text-red-700'
                                }`}>
                                    {message}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* New Password */}
                                <div>
                                    <label className="text-xs font-semibold text-gray-500 uppercase ml-1">New Password</label>
                                    <div className="relative mt-1 group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <Lock className="text-gray-400 transition-colors group-focus-within:text-[#134DA7]" size={16} />
                                        </div>
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="••••••••"
                                            className="w-full pl-11 pr-11 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-[#134DA7] outline-none transition-colors"
                                            required
                                            minLength={8}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-slate-600 transition-colors p-1"
                                        >
                                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>
                                </div>

                                {/* Confirm Password */}
                                <div>
                                    <label className="text-xs font-semibold text-gray-500 uppercase ml-1">Confirm Password</label>
                                    <div className="relative mt-1 group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <ShieldCheck className="text-gray-400 transition-colors group-focus-within:text-[#134DA7]" size={16} />
                                        </div>
                                        <input
                                            type={showConfirmPassword ? "text" : "password"}
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            placeholder="••••••••"
                                            className="w-full pl-11 pr-11 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-[#134DA7] outline-none transition-colors"
                                            required
                                            minLength={8}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-slate-600 transition-colors p-1"
                                        >
                                            {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full py-3 bg-[#134DA7] hover:bg-blue-700 text-white font-bold rounded-xl text-sm transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-6"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            <span>Updating...</span>
                                        </>
                                    ) : (
                                        <span>Update Password</span>
                                    )}
                                </button>
                            </form>

                            {/* Back Link */}
                            <div className="mt-6 text-center">
                                <Link 
                                    to="/" 
                                    className="inline-flex items-center gap-2 text-xs font-bold text-blue-600 hover:text-blue-700 transition-all group"
                                >
                                    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                                    <span>Back to Login</span>
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* Footer Copy */}
                    <p className="text-center text-xs text-gray-400 mt-6">
                        &copy; 2026 EasyMarg. All rights reserved.
                    </p>
                </motion.div>
            </div>
        </div>
    );
};

export default ResetPassword;